import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import { GlassIconButton } from './glass-icon-button';

/**
 * Lớp phủ + hộp thoại, một chỗ.
 *
 * ## Vì sao tách ra
 *
 * Trước APM-ROLE-s có **năm** chỗ tự viết cùng một khối `fixed inset-0` +
 * `role="dialog"`. Chúng đã bắt đầu trôi khỏi nhau: chỗ đóng được bằng Esc, chỗ
 * không; chỗ có `aria-modal`, chỗ quên. Một hộp thoại không đóng được bằng Esc
 * là thứ chỉ người dùng bàn phím phát hiện ra, và họ không báo lỗi — họ bỏ đi.
 *
 * Đã chuyển sang dùng chung: `MyInvitations`, `LeaveProjectPanel`, và hộp thoại
 * tài liệu ở đây. **Chưa chuyển:** `task-form-dialog`, `task-detail-drawer`
 * (là drawer trượt cạnh, khác hình dạng) và `guided-tour` (bong bóng bám theo
 * phần tử, không phải hộp thoại giữa màn hình).
 *
 * ## Vì sao cổng ra `document.body`
 *
 * `position: fixed` chỉ bám **khung nhìn** khi không tổ tiên nào có `transform`,
 * `filter` hay `backdrop-filter` — có một cái là nó bám vào tổ tiên ấy. Chuông
 * thông báo nằm trong `.glass-bar` (kính mờ, APM-NAV-a), nên hộp thư canh giữa
 * theo viên thanh cao 38px: `top-1/2 -translate-y-1/2` đẩy nửa hộp lên khỏi mép
 * trên màn hình, còn lớp phủ `inset-0` chỉ phủ đúng viên thanh.
 */
export function Dialog({
  label,
  onClose,
  size = 'md',
  fixedHeight = false,
  className,
  children,
}: {
  /** `aria-label` của hộp — thứ trình đọc màn hình đọc lên khi hộp mở ra. */
  readonly label: string;
  readonly onClose: () => void;
  /**
   * `lg` cho hộp chứa cả một màn hình (danh sách tài liệu, bảng). `xl` cho hộp
   * có bảng nhiều cột cần đọc ngang — xem trước file nhập (APM-TIMP). `full`
   * gần kín màn hình (chừa 1rem mỗi mép để còn thấy mình đang ở trong một hộp)
   * — cho nút "Mở rộng tối đa" của hộp xem tài liệu; chiều cao cố định, nội
   * dung tự lấp bằng `flex-1`.
   */
  readonly size?: 'md' | 'lg' | 'xl' | 'full';
  /**
   * Giữ một chiều cao cố định thay vì co theo nội dung — cho hộp mà nội dung đổi
   * liên tục (đổi tab, danh sách tải xong, mở form): khung nhảy kích thước theo
   * từng bước trông như lỗi. Nội dung dài hơn thì cuộn bên trong.
   */
  readonly fixedHeight?: boolean;
  /**
   * Lớp thêm cho khung hộp — cho hộp cần bố cục riêng theo cỡ màn (vd hộp chuyển `.chilldoc`
   * cao cố định hai cột ở desktop). Đứng cuối nên ghi đè được cỡ mặc định.
   */
  readonly className?: string;
  readonly children: ReactNode;
}): ReactNode {
  const { t } = useTranslation();
  const box = useRef<HTMLDivElement>(null);
  const [applySlot, setApplySlot] = useState<HTMLSpanElement | null>(null);

  // Esc để đóng — thao tác ai cũng thử đầu tiên khi muốn thoát một lớp phủ.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      /*
        Chỉ lớp **trên cùng** đóng (APM-PDOC-d). Hộp hỏi "Xoá tài liệu?" mở từ
        trong một hộp khác, hay hộp soạn toàn màn hình mở từ popup xem trước:
        Esc ở lớp trên mà đóng luôn lớp dưới là gỡ cả lớp trên theo — và với
        hộp soạn, là mất chữ chưa lưu. Cùng luật với `editorIsTopLayer`.
      */
      const modals = document.querySelectorAll('[aria-modal="true"]');
      if (modals.length > 0 && modals[modals.length - 1] !== box.current) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <>
      <div className="fixed inset-0 z-40 popup-scrim" onClick={onClose} aria-hidden />

      <div
        ref={box}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        className={cn(
          'dialog-sheet fixed left-1/2 top-1/2 z-50 flex max-h-[85vh] w-full -translate-x-1/2 -translate-y-1/2 flex-col gap-3 overflow-y-auto rounded-DEFAULT border border-border bg-background p-4 shadow-xl',
          size === 'full'
            ? 'h-[calc(100dvh-2rem)] max-h-none w-[calc(100vw-2rem)] max-w-none'
            : size === 'xl'
              ? 'max-w-6xl glass-popup-xl on-scrim'
              : size === 'lg'
                ? 'max-w-4xl glass-popup-lg on-scrim'
                : 'max-w-lg glass-popup-md on-scrim',
          // Điện thoại: chừa 12px mỗi bên — hộp `w-full` dính sát hai mép màn hình trông như
          // trang bị xô lệch, không như một lớp nổi (APM-MBAR-e).
          size !== 'full' && 'max-md:w-[calc(100%-1.5rem)]',
          // Con không co lại: thiếu chỗ thì hộp cuộn, không bóp nội dung chồng lên nhau.
          fixedHeight && 'h-[min(85vh,36rem)] [&>*]:shrink-0',
          className,
        )}
      >
        {/*
          Nút đóng ở góc phải trên — APM-ORG-e4.

          Esc và bấm ra ngoài đã đóng được, nhưng cả hai đều là thứ **phải biết
          trước**. Một dấu X là thứ người dùng nhìn thấy, và nó là quy ước mà
          mọi hộp thoại trên đời đều theo.

          `sticky` chứ không `absolute`: hộp này cuộn được (`overflow-y-auto`),
          và một nút `absolute` sẽ trôi lên khỏi khung nhìn ngay khi nội dung
          dài hơn màn hình — đúng lúc người ta cần nó nhất.
        */}
        {/* Nút tròn kính kiểu sheet iOS 26 (2026-10-05), cao 28px.

            Hàng nút chiếm **dòng riêng** — bản trước kéo nội dung lên đè dưới
            nó (`-mb-4`), và hộp nào có nút ở góc phải trên ("Nháp mới" của Kế
            hoạch nháp) thì bị ✕ đè lên (2026-10-05). Tiêu đề đứng đầu hộp thì
            `.dialog-sheet` kéo nó lên ngang hàng nút và chừa chỗ bên phải —
            xem `index.css`. */}
        {/* ✓ đứng **trước**, ✕ ở mép phải — người dùng chốt 2026-10-05: nút
            đóng luôn ở góc phải trên, cùng một chỗ dù hộp có ✓ hay không. */}
        <div className="sticky top-0 z-10 flex items-center gap-2 self-end">
          {/* Khe cho nút ✓ — `<DialogApply>` ở bất cứ đâu bên trong cổng vào đây. */}
          <span ref={setApplySlot} className="contents" />
          <GlassIconButton
            variant="close"
            onClick={onClose}
            // Nhãn của **nút**, không phải của hộp: đọc lên tên hộp ở đây thì
            // trình đọc màn hình nói "Bùi Ngọc Lan, nút" cho một nút đóng.
            label={t('common.close')}
          />
        </div>

        <ApplySlotContext.Provider value={applySlot}>{children}</ApplySlotContext.Provider>
      </div>
    </>,
    document.body,
  );
}

const ApplySlotContext = createContext<HTMLElement | null>(null);

/**
 * Nút ✓ "áp dụng" ở góc trên, cạnh ✕ — sheet kiểu iOS 26 (người dùng chốt
 * 2026-10-05). Đặt ở **bất kỳ đâu** bên trong `<Dialog>`, kể cả trong form con
 * giữ state của nút lưu: nó cổng lên khe ở đầu hộp.
 *
 * Dùng thay cặp nút chữ "Lưu / Huỷ" ở chân hộp tạo/sửa đơn giản — ✕ đã là Huỷ.
 * ⚠️ **Không** dùng cho hành động cần đọc chữ mới hiểu hậu quả: Xoá, "Áp dụng
 * 3 thay đổi", chạy AI có tính tiền. Một dấu ✓ không nói được những điều đó.
 *
 * Nút nằm **ngoài** `<form>` (cổng ra đầu hộp), nên nộp form bằng `form={id}`
 * + `type="submit"` chứ không bằng việc nằm trong form. `label` là chữ của nút
 * cũ ("Lưu", "Tạo") — nó thành `aria-label` và `title`.
 */
export function DialogApply({
  label,
  disabled,
  pending = false,
  onClick,
  form,
}: {
  readonly label: string;
  readonly disabled?: boolean;
  /** Đang lưu: nút khoá và hiện vòng quay thay dấu ✓. */
  readonly pending?: boolean;
  readonly onClick?: () => void;
  /** `id` của `<form>` cần nộp — có thì nút là `type="submit"`. */
  readonly form?: string;
}): ReactNode {
  const slot = useContext(ApplySlotContext);
  if (!slot) return null;
  return createPortal(
    <GlassIconButton
      variant="apply"
      label={label}
      pending={pending}
      disabled={disabled === true || pending}
      onClick={onClick}
      type={form ? 'submit' : 'button'}
      form={form}
    />,
    slot,
  );
}
